/**
 * Nov 4 field test (P2) — parking-lot image setup a shop owner can finish.
 *
 * Floyd's upload succeeded but calibration spun forever: the views loaded the stored image with no `onerror`, no
 * timeout and no retry, so any failure (signed URL refused, network, an image the browser cannot decode, a photo too
 * large for a phone/tablet canvas) was an endless spinner. Upload also demanded at least 3000×2000 px, which ordinary
 * phone screenshots and satellite captures fail, while very large drone photos passed and then strained the browser.
 *
 * Now: any practical image is accepted (≥ 800×500), large or non-JPEG/PNG images are optimized in the browser before
 * upload (longest edge ≤ 4096 px, JPEG), and loading reports an actionable reason.
 */

export const MIN_LOT_IMAGE_LONG_EDGE = 800;
export const MIN_LOT_IMAGE_SHORT_EDGE = 500;
/** Longest stored edge. 4096×3072 ≈ 12.6 MP — under the ~16.7 MP canvas ceiling of iOS/iPadOS Safari. */
export const MAX_LOT_IMAGE_EDGE = 4096;
/** Files above this are re-encoded even when their dimensions are fine. */
export const OPTIMIZE_ABOVE_BYTES = 8 * 1024 * 1024;
/** The picker's raw-file ceiling (the bucket stores at most 50 MB; optimized output is far smaller). */
export const MAX_SOURCE_BYTES = 100 * 1024 * 1024;
export const LOT_IMAGE_LOAD_TIMEOUT_MS = 30_000;

const STORED_AS_IS = ['image/jpeg', 'image/png'];

export interface LotImagePlan {
  ok: boolean;
  error?: string;
  width: number;
  height: number;
  /** Re-encode (resize and/or convert to JPEG) before upload. */
  optimize: boolean;
}

/** Pure: may this decoded image be used, and does it need optimizing first? */
export function planLotImage(input: { width: number; height: number; type: string; size: number }): LotImagePlan {
  const { width, height, type, size } = input;
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  if (long < MIN_LOT_IMAGE_LONG_EDGE || short < MIN_LOT_IMAGE_SHORT_EDGE) {
    return {
      ok: false,
      width,
      height,
      optimize: false,
      error: `This image is ${width}×${height} pixels — too small to place parking spots on. Use a photo or satellite `
        + `capture at least ${MIN_LOT_IMAGE_LONG_EDGE}×${MIN_LOT_IMAGE_SHORT_EDGE}.`,
    };
  }
  const factor = long > MAX_LOT_IMAGE_EDGE ? MAX_LOT_IMAGE_EDGE / long : 1;
  const outW = Math.round(width * factor);
  const outH = Math.round(height * factor);
  const optimize = factor < 1 || !STORED_AS_IS.includes(type) || size > OPTIMIZE_ABOVE_BYTES;
  return { ok: true, width: outW, height: outH, optimize };
}

export type LotImageLoadFailure = 'signed_url' | 'decode' | 'timeout';

export function lotImageLoadErrorMessage(kind: LotImageLoadFailure, detail?: string): string {
  switch (kind) {
    case 'signed_url':
      return `We couldn't open the stored parking-lot image${detail ? ` (${detail})` : ''}. Check your connection and `
        + 'try again. If it keeps failing, upload the image again.';
    case 'timeout':
      return 'The parking-lot image is taking too long to load. Check your connection and try again, or upload a '
        + 'smaller image.';
    case 'decode':
    default:
      return 'This browser could not display the stored parking-lot image (it may be too large or in an unsupported '
        + 'format). Upload it again — it will be optimized automatically.';
  }
}

export class LotImageLoadError extends Error {
  constructor(public readonly kind: LotImageLoadFailure, detail?: string) {
    super(lotImageLoadErrorMessage(kind, detail));
    this.name = 'LotImageLoadError';
  }
}

/**
 * Load a stored image from its signed URL with a real failure path (HTTP error, network, decode, timeout all reject).
 *
 * NOV4 P2 ROOT CAUSE: production's Content-Security-Policy allows `img-src 'self' data: blob: https://lovable.dev` — NOT
 * the Supabase storage origin — so `<img src="https://…supabase.co/storage/…">` is blocked by the browser (and, with
 * no `onerror`, spun forever). The bytes are therefore FETCHED (`connect-src` allows the Supabase origin) and drawn
 * from a `blob:` URL (`img-src` allows `blob:`). A same-origin blob also keeps the canvas untainted.
 */
export async function loadLotImage(url: string, timeoutMs = LOT_IMAGE_LOAD_TIMEOUT_MS): Promise<HTMLImageElement> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  let blob: Blob;
  try {
    const res = await fetch(url, { signal: abort.signal });
    if (!res.ok) throw new LotImageLoadError('signed_url', `HTTP ${res.status}`);
    blob = await res.blob();
  } catch (err) {
    clearTimeout(timer);
    if (err instanceof LotImageLoadError) throw err;
    if (abort.signal.aborted) throw new LotImageLoadError('timeout');
    throw new LotImageLoadError('signed_url', 'network error');
  }
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const done = () => { clearTimeout(timer); URL.revokeObjectURL(objectUrl); };
    abort.signal.addEventListener('abort', () => {
      img.onload = null;
      img.onerror = null;
      done();
      reject(new LotImageLoadError('timeout'));
    });
    img.onload = () => {
      done();
      if (!img.naturalWidth || !img.naturalHeight) reject(new LotImageLoadError('decode'));
      else resolve(img);
    };
    img.onerror = () => {
      done();
      reject(new LotImageLoadError('decode'));
    };
    img.src = objectUrl;
  });
}

/**
 * What to draw for a stored image: the image itself, or — when it is larger than a canvas can safely hold — a
 * downscaled copy. Callers always draw it at the stored NATURAL size (`drawImage(src, 0, 0, naturalW, naturalH)`), so
 * calibration points and spot polygons keep their stored pixel coordinates.
 */
export function displaySourceFor(img: HTMLImageElement): CanvasImageSource {
  const long = Math.max(img.naturalWidth, img.naturalHeight);
  if (long <= MAX_LOT_IMAGE_EDGE) return img;
  const factor = MAX_LOT_IMAGE_EDGE / long;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * factor);
  canvas.height = Math.round(img.naturalHeight * factor);
  const ctx = canvas.getContext('2d');
  if (!ctx) return img;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export interface PreparedLotImage {
  blob: Blob;
  width: number;
  height: number;
  extension: 'jpg' | 'png';
  contentType: string;
  optimized: boolean;
}

function decodeFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(
        /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
          ? 'This browser cannot read HEIC photos. On iPhone, set Camera › Formats to "Most Compatible", or share the '
            + 'photo as JPG, then upload again.'
          : "This file couldn't be read as an image. Upload a JPG or PNG photo of your lot.",
      ));
    };
    img.src = url;
  });
}

/** Decode, validate and (when needed) optimize a chosen file into what gets stored. */
export async function prepareLotImage(file: File): Promise<PreparedLotImage> {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('This file is over 100 MB. Use a smaller export of the photo.');
  }
  const img = await decodeFile(file);
  const plan = planLotImage({ width: img.naturalWidth, height: img.naturalHeight, type: file.type, size: file.size });
  if (!plan.ok) throw new Error(plan.error);
  if (!plan.optimize) {
    return {
      blob: file,
      width: plan.width,
      height: plan.height,
      extension: file.type === 'image/png' ? 'png' : 'jpg',
      contentType: file.type,
      optimized: false,
    };
  }
  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not prepare the image. Try a JPG under 8 MB.');
  ctx.drawImage(img, 0, 0, plan.width, plan.height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('This browser could not prepare the image. Try a JPG under 8 MB.');
  return { blob, width: plan.width, height: plan.height, extension: 'jpg', contentType: 'image/jpeg', optimized: true };
}
